import { useAppDispatch } from '_store';
import { pmvsSagas } from '_store/pmv/sagas';
import { enrolledVmsSelector } from '_store/pmv/selectors';
import { useLocalization } from '@fluent/react';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { usePmvL10n } from '../../../hooks/usePmvL10n';
import { useRolePermissions } from '../../../hooks/useRolePermissions';
import MessageDialog from '../../items/MessageDialog';
import PmvCardListItem from './Card';

export interface PmvLayoutSingleProps {
  id: string;
  onUpdatePmv?: (idExpanded: string) => void;
}

const PmvLayoutSingle: FC<PmvLayoutSingleProps> = ({ id, onUpdatePmv }) => {
  const { l10n } = useLocalization();
  const { getPmvString } = usePmvL10n();
  const enrolledVms = useSelector(enrolledVmsSelector);
  const vms = enrolledVms.find((item) => item.id === id);
  const dispatch = useAppDispatch();
  const { canManagePmv, blockedActionMode } = useRolePermissions();
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const [showMessage, setShowMessage] = useState({
    active: false,
    typeAction: 'delete',
    idToDelete: null as string | null,
    typeEvent: 'pmv',
  });

  useEffect(() => {
    dispatch(pmvsSagas.actions.vmsRequest());
  }, [dispatch]);

  if (!vms) {
    return <>{getPmvString('forms-pmv-not-found')}</>;
  }

  const onMessageLeaveConfirmed = (type: string) => {
    if (type === 'confirm' && showMessage.idToDelete) {
      dispatch(pmvsSagas.actions.deleteVms(showMessage.idToDelete));
    }

    setShowMessage({
      active: false,
      idToDelete: null,
      typeAction: 'delete',
      typeEvent: 'pmv',
    });
  };

  const onDeletePmv = (idToDelete: string) => {
    setShowMessage({
      active: true,
      idToDelete,
      typeAction: 'delete',
      typeEvent: 'pmv',
    });
  };

  return (
    <>
      {showMessage.active ? (
        <MessageDialog statusMessage={showMessage} onCloseDialog={onMessageLeaveConfirmed} />
      ) : null}
      <PmvCardListItem
        vms={vms}
        asCard={false}
        onUpdate={() => onUpdatePmv?.(vms.id)}
        onDelete={() => onDeletePmv(vms.id)}
        writeAllowed={canManagePmv}
        blockedActionMode={blockedActionMode}
        blockedMessage={writeBlockedMessage}
      />
    </>
  );
};

export default React.memo(PmvLayoutSingle);
